import { saveAs } from "file-saver";
import JSZip from "jszip";
import { flatten, isEmpty, keyBy, orderBy, sortBy } from "lodash";
import moment from "moment";
import { unparse } from "papaparse";

import {
  type Kpi,
  KpiType,
  type ModelMetadataKpiFormula,
} from "@features/mmm/components/explore-budgets/client-setting/types";
import { type ChannelInsightsSorting } from "@features/mmm/hooks/mmm-url-state";
import { efficiencyKpiManager } from "@features/mmm/utils/efficiency-kpi-manager";
import { getCurrencySymbol } from "@features/mmm/utils/formatters";
import { type User } from "@internal/user";
import { formatting, type CurrencyCode } from "@north-beam/nb-common";

import {
  EFFICIENCY_OVER_TIME,
  SPEND_VS_REVENUE,
  type ExportSelection,
} from "../../export-channel-insights-modal/export-channel-insights-modal";
import { type ChannelInsightsColumn } from "../../model-insights-content";

enum DataType {
  EFFICIENCY_METRIC,
  SPEND,
  TOTAL_KPI,
  PROMO_ONLY,
  KPI_ONLY,
}

function getDataTypeLabel(
  dataType: DataType,
  isCurrency: boolean,
  currencyCode: CurrencyCode,
) {
  switch (dataType) {
    case DataType.EFFICIENCY_METRIC:
      return isCurrency
        ? "ROAS (x)"
        : `CAC (${getCurrencySymbol(currencyCode)})`;
    case DataType.SPEND:
      return `SPEND (${getCurrencySymbol(currencyCode)})`;
    case DataType.TOTAL_KPI:
      return isCurrency
        ? `TOTAL_KPI (${getCurrencySymbol(currencyCode)})`
        : "TOTAL_KPI (Count)";
    case DataType.KPI_ONLY:
      return isCurrency
        ? `KPI_ONLY (${getCurrencySymbol(currencyCode)})`
        : "KPI_ONLY (Count)";
    case DataType.PROMO_ONLY:
      return isCurrency
        ? `PROMO_ONLY (${getCurrencySymbol(currencyCode)})`
        : "PROMO_ONLY (Count)";
  }
}

export const exportModelFitToCsv = (
  csvFileName: string,
  modelFit: any[],
  formatter: (arg: number) => string,
) => {
  const modelFitData = modelFit.map(({ date, data }) => [
    moment(date).format("MMM DD, YYYY"),
    formatter(data.actual),
    formatter(data.prediction_point_total),
    formatter(data.prediction_band_lo),
    formatter(data.prediction_band_hi),
    formatter(data.prediction_point_baseline),
  ]);

  const modelFitCsv = unparse({
    fields: [
      "Date",
      "Actual",
      "Predicted",
      "Prediction Low",
      "Prediction High",
      "Baseline",
    ],
    data: modelFitData,
  });

  const modelFitBlob = new Blob([modelFitCsv], {
    type: "text/plain;charset=utf-8",
  });

  saveAs(modelFitBlob, `${csvFileName}.csv`);
};

export const sortHistoricalDataset = ({
  user,
  columns,
  selectedKpi,
  sorting,
}: {
  user: User;
  columns: ChannelInsightsColumn[];
  selectedKpi: ModelMetadataKpiFormula | undefined;
  sorting: ChannelInsightsSorting;
}) => {
  const columnsCopy: (ChannelInsightsColumn & {
    _valueForSorting?: string | number;
  })[] = [...columns];

  const { efficiencyKpiCalculator } = efficiencyKpiManager(
    selectedKpi as Kpi,
    user,
  );
  columnsCopy.forEach((col) => {
    let valueForSorting: number | string = col.column_name;
    if (sorting.column === "spend") {
      valueForSorting = col.spend;
    } else if (sorting.column === "kpi") {
      valueForSorting = col.kpi;
    } else if (sorting.column === "efficiency") {
      const efficiency = efficiencyKpiCalculator(col.spend, col.kpi);
      // Sorts Infinity values to the end
      if (sorting.direction === "asc") {
        valueForSorting = efficiency === Infinity ? Infinity : efficiency;
      } else {
        valueForSorting = efficiency === Infinity ? -Infinity : efficiency;
      }
    }
    col._valueForSorting = valueForSorting;
  });

  return orderBy(columnsCopy, "_valueForSorting", sorting.direction);
};

export function curateHistoricalDataset<T>(
  rows: T[] | undefined | null,
  dateRange: string,
) {
  rows = rows ?? [];
  const numberOfRows = rows.length;

  const dateRangeToValue: Record<
    string,
    { startWeek: number; endWeek: number }
  > = {
    last_4_weeks: {
      startWeek: numberOfRows - 4,
      endWeek: numberOfRows,
    },
    last_5_weeks: {
      startWeek: numberOfRows - 5,
      endWeek: numberOfRows,
    },
    last_26_weeks: {
      startWeek: numberOfRows - 26,
      endWeek: numberOfRows,
    },
    last_52_weeks: {
      startWeek: numberOfRows - 52,
      endWeek: numberOfRows,
    },
    last_year: {
      startWeek: numberOfRows - 52 * 2,
      endWeek: numberOfRows - 52,
    },
  };

  const rowsToUse =
    !dateRange || dateRange === "all_time"
      ? rows
      : rows.slice(
          dateRangeToValue[dateRange].startWeek,
          dateRangeToValue[dateRange].endWeek,
        );
  return rowsToUse;
}

type DataToZip = Record<
  string,
  { fields: string[]; data: (string | number)[][] }
>;

export const exportChannelInsightsToCsv = async (
  dateRange: string,
  exportSelection: ExportSelection,
  columns: any[],
  selectedKpi: ModelMetadataKpiFormula,
  historicalChannelContributionsData: any,
  costCurvesData: any,
  user: User,
) => {
  const code = user.currencies.currentCurrency;
  const {
    kpiFormatter,
    efficiencyKpiCalculator,
    efficiencyKpiFormatter,
    efficiencyKpiType,
  } = efficiencyKpiManager(selectedKpi as Kpi, user);
  const columnMap = keyBy(columns, "column_name");
  const dataToZip: DataToZip = {};
  const isRevenue = selectedKpi.kpi_type === KpiType.REVENUE;

  const kpiIncrementalOverTime: Record<string, Record<string, any>> = {};

  Object.entries(exportSelection).forEach(([channel, selection]) => {
    const channelName =
      columnMap[channel]?.breakdowns
        .map(({ value }: any) => value)
        .join(" / ") ?? "Unknown";

    if (selection[EFFICIENCY_OVER_TIME]) {
      const rows: {
        kpiData: Record<string, number>;
        spend: number;
      }[] = costCurvesData[channel];

      const data = rows.map((row) => {
        const value = row.kpiData[selectedKpi.kpi_name];
        const efficiencyValue = efficiencyKpiCalculator(row.spend, value);
        return [
          formatting.formatCurrency(row.spend, { code }),
          kpiFormatter(value, "exact"),
          efficiencyKpiFormatter(efficiencyValue, "exact"),
        ];
      });

      dataToZip[`${channelName} - Spend vs Revenue`] = {
        fields: ["Spend", selectedKpi.friendly_name, efficiencyKpiType],
        data,
      };
    }

    if (selection[SPEND_VS_REVENUE]) {
      const rows = curateHistoricalDataset<{
        date: string;
        kpiData: Record<string, number>;
        promoData: Record<string, number>;
        spend: number;
      }>(historicalChannelContributionsData[channel], dateRange);

      const spendData = ["Spend"];
      const kpiData: (string | number)[] = [
        `${selectedKpi.friendly_name} (total)`,
      ];
      const kpiOnlyData = [`${selectedKpi.friendly_name} (kpi only)`];
      const promoOnlyData = [`${selectedKpi.friendly_name} (promotions only)`];
      const efficiencyMetricData: (string | number)[] = [efficiencyKpiType];
      const fields = [""];

      rows.forEach((row) => {
        const dateString = moment(row.date).format("YYYY-MM-DD");
        fields.push(dateString);

        const value = row.kpiData[selectedKpi.kpi_name];
        const promoValue = row.promoData[selectedKpi.kpi_name];
        const totalContribution = value + promoValue;
        const efficiencyKpi = efficiencyKpiCalculator(
          row.spend,
          totalContribution,
        );

        spendData.push(formatting.formatCurrency(row.spend, { code }));
        kpiData.push(kpiFormatter(totalContribution, "exact"));
        kpiOnlyData.push(kpiFormatter(value, "exact"));
        promoOnlyData.push(kpiFormatter(promoValue, "exact"));
        efficiencyMetricData.push(
          efficiencyKpiFormatter(efficiencyKpi, "exact"),
        );

        kpiIncrementalOverTime[dateString] ||= {};
        kpiIncrementalOverTime[dateString][channelName] = {
          kpi: kpiFormatter(totalContribution, "exact"),
          kpiEfficiency: efficiencyKpiFormatter(efficiencyKpi, "exact"),
          kpiOnly: kpiFormatter(value, "exact"),
          kpiPromos: kpiFormatter(promoValue, "exact"),
          spend: formatting.formatCurrency(row.spend, { code }),
        };
      });

      dataToZip[`${channelName} - Incremental Performance Over Time`] = {
        fields,
        data: [
          spendData,
          kpiData,
          promoOnlyData,
          kpiOnlyData,
          efficiencyMetricData,
        ],
      };
    }

    if (!isEmpty(kpiIncrementalOverTime)) {
      const dataTypes = [
        DataType.TOTAL_KPI,
        DataType.KPI_ONLY,
        DataType.PROMO_ONLY,
        DataType.SPEND,
        DataType.EFFICIENCY_METRIC,
      ];
      const dataTypeToSuffixMap: Record<DataType, string> = {
        [DataType.TOTAL_KPI]: "(total)",
        [DataType.KPI_ONLY]: "(kpi only)",
        [DataType.PROMO_ONLY]: "(promotions only)",
        [DataType.SPEND]: "Spend",
        [DataType.EFFICIENCY_METRIC]: `(${efficiencyKpiType})`,
      };
      const namingMapping: Record<DataType, string> = {
        [DataType.TOTAL_KPI]: "kpi",
        [DataType.KPI_ONLY]: "kpiOnly",
        [DataType.PROMO_ONLY]: "kpiPromos",
        [DataType.EFFICIENCY_METRIC]: "kpiEfficiency",
        [DataType.SPEND]: "spend",
      };

      const dataset: Record<string, string[]> = {};
      const dates = sortBy(Object.keys(kpiIncrementalOverTime));

      dates.forEach((date) => {
        Object.entries(kpiIncrementalOverTime[date]).forEach(
          ([kpi, kpiDetails]) => {
            dataTypes.forEach((dataType) => {
              const kpiNaming = `${kpi} ${dataTypeToSuffixMap[dataType]}`;
              if (!dataset[kpiNaming]) {
                dataset[kpiNaming] = [
                  kpiNaming,
                  getDataTypeLabel(dataType, isRevenue, code),
                ];
              }
              dataset[kpiNaming].push(kpiDetails[namingMapping[dataType]]);
            });
          },
        );
      });

      dataToZip[`${selectedKpi.friendly_name} Summary Over Time`] = {
        fields: ["", "Data type", ...dates],
        data: flatten(
          dataTypes.map((dt) =>
            sortBy(Object.keys(dataset)).filter((k) =>
              k.endsWith(dataTypeToSuffixMap[dt]),
            ),
          ),
        ).map((k) => dataset[k]),
      };
    }
  });

  const zip = JSZip();
  Object.entries(dataToZip).forEach(([filename, { fields, data }]) => {
    zip.file(`${filename}.csv`, unparse({ fields, data }));
  });

  const zipFile = await zip.generateAsync({ type: "blob" });
  saveAs(zipFile, `${selectedKpi.friendly_name}_channel_insights.zip`);
};
